import { useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { CalendarIcon, Search, ExternalLink, CalendarDays } from "lucide-react";
import { AppHeader } from "@/components/app/AppHeader";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { useAuth, DEPARTMENT_LABELS, type Department } from "@/lib/auth-context";
import { useAllProfiles } from "@/hooks/use-admin-data";
import { useTeamEntries, type TeamEntry } from "@/hooks/use-team-entries";
import { todayIstIso } from "@/lib/tz";
import { modCanWorkspace, type ModeratorPermissions } from "@/lib/moderator-permissions";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_app/today-tasks")({
  component: TodayTasksPage,
  head: () => ({ meta: [{ title: "Today's Task" }] }),
});

const DEPT_OPTIONS: { value: "all" | Department; label: string }[] = [
  { value: "all", label: "All Departments" },
  { value: "editor", label: "Editors" },
  { value: "graphic_designer", label: "Graphic Designer" },
  { value: "ai", label: "AI" },
  { value: "scriptwriter", label: "Scriptwriter" },
  { value: "marketer", label: "Marketing" },
  { value: "seo", label: "SEO" },
  { value: "smm", label: "SMM" },
  { value: "calling", label: "Calling" },
  { value: "hr", label: "HR" },
];

const STATUS_OPTIONS = [
  { value: "all", label: "All Status" },
  { value: "completed", label: "Completed" },
  { value: "in_progress", label: "In Progress" },
  { value: "pending", label: "Pending" },
  { value: "no_update", label: "No Update" },
] as const;

const SORT_OPTIONS = [
  { value: "latest", label: "Latest Activity" },
  { value: "most", label: "Most Tasks" },
  { value: "least", label: "Least Tasks" },
  { value: "name", label: "Name A-Z" },
] as const;
type SortKey = (typeof SORT_OPTIONS)[number]["value"];

type UiStatus = "completed" | "in_progress" | "pending" | "no_update" | "missing";

const NO_HOURS_DEPTS: Department[] = ["editor", "graphic_designer", "ai", "scriptwriter"];

const ROLE_LABELS: Partial<Record<Department, string>> = {
  editor: "Content Editor",
  graphic_designer: "Designer",
  ai: "AI",
  scriptwriter: "Scriptwriter",
  marketer: "Marketer",
  seo: "SEO Executive",
  smm: "SMM Executive",
  calling: "Calling Agent",
  hr: "HR Executive",
  admin: "Admin",
};

const NO_STATUS_TABLES = ["calling_submissions", "calling_other_tasks"];

function classifyEntryStatus(e: TeamEntry): UiStatus {
  const raw = (e.details as Record<string, unknown> | undefined)?.raw as
    | Record<string, unknown>
    | undefined;
  const table = String(
    e.source_table ?? (e.details as Record<string, unknown> | undefined)?.source_table ?? "",
  );
  if (NO_STATUS_TABLES.includes(table)) return "completed";
  const s = String(raw?.status ?? "").toLowerCase();
  if (s === "completed" || s === "done" || e.status === "approved") return "completed";
  if (s === "work_in_progress" || s === "in_progress") return "in_progress";
  if (s === "") return "completed";
  return "pending";
}

function pickLatestStatus(entries: TeamEntry[]): UiStatus {
  if (!entries.length) return "no_update";
  // sort by created_at desc
  const sorted = [...entries].sort((a, b) =>
    (a.created_at ?? "") < (b.created_at ?? "") ? 1 : -1,
  );
  return classifyEntryStatus(sorted[0]);
}

function parseHHMM(t: unknown): number | null {
  if (!t || typeof t !== "string") return null;
  const m = t.match(/^(\d{1,2}):(\d{2})/);
  if (!m) return null;
  return Number(m[1]) * 60 + Number(m[2]);
}

function totalMinutes(entries: TeamEntry[]): number {
  let mins = 0;
  for (const e of entries) {
    const raw = (e.details as Record<string, unknown> | undefined)?.raw as
      | Record<string, unknown>
      | undefined;
    const s = parseHHMM(raw?.start_time);
    const en = parseHHMM(raw?.end_time);
    if (s != null && en != null && en > s) mins += en - s;
  }
  return mins;
}

function fmtHM(mins: number): string {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${h}h ${String(m).padStart(2, "0")}m`;
}

const STATUS_PILL: Record<UiStatus, { bg: string; fg: string; label: string }> = {
  completed: { bg: "#DCFCE7", fg: "#16A34A", label: "Completed" },
  in_progress: { bg: "#EDE9FE", fg: "#7C3AED", label: "In Progress" },
  pending: { bg: "#FEF3C7", fg: "#B45309", label: "Pending" },
  no_update: { bg: "#F3F4F6", fg: "#6B7280", label: "No Update" },
  missing: { bg: "#FEE2E2", fg: "#DC2626", label: "Missing" },
};

function TodayTasksPage() {
  const { profile, role, loading } = useAuth();
  const today = todayIstIso();
  const permsForScope = (profile?.moderator_permissions ?? {}) as ModeratorPermissions;
  const ownDeptLock =
    role === "moderator" && Boolean(permsForScope.scope_own_department) && profile?.department
      ? (profile.department as Department)
      : null;

  // applied filters
  const [date, setDate] = useState(today);
  const [dept, setDept] = useState<"all" | Department>(ownDeptLock ?? "all");
  const [status, setStatus] = useState<(typeof STATUS_OPTIONS)[number]["value"]>("all");
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("latest");
  const [openId, setOpenId] = useState<string | null>(null);

  // pending (not yet applied) values
  const [pDate, setPDate] = useState(today);
  const [pDept, setPDept] = useState<"all" | Department>(ownDeptLock ?? "all");
  const [pStatus, setPStatus] = useState<(typeof STATUS_OPTIONS)[number]["value"]>("all");
  const [pSearch, setPSearch] = useState("");

  const { data: profiles = [] } = useAllProfiles();
  const { data: entries = [], isLoading } = useTeamEntries({ date });

  const entriesForDate = useMemo(
    () => entries.filter((e) => e.submission_date === date),
    [entries, date],
  );

  // group by user
  const byUser = useMemo(() => {
    const m = new Map<string, TeamEntry[]>();
    for (const e of entriesForDate) {
      const arr = m.get(e.user_id) ?? [];
      arr.push(e);
      m.set(e.user_id, arr);
    }
    return m;
  }, [entriesForDate]);

  // filter active employees by department selection
  const visibleEmployees = useMemo(() => {
    return profiles
      .filter((p) => (p.is_active ?? p.status === "active"))
      .filter((p) => (dept === "all" ? true : p.department === dept));
  }, [profiles, dept]);

  // build per-employee summary
  const cards = useMemo(() => {
    const term = search.trim().toLowerCase();
    const built = visibleEmployees
      .map((p) => {
        const list = byUser.get(p.id) ?? [];
        const stat = pickLatestStatus(list);
        const sorted = [...list].sort((a, b) =>
          (a.created_at ?? "") < (b.created_at ?? "") ? 1 : -1,
        );
        const latest = sorted[0] ?? null;
        const minutes = totalMinutes(list);
        const showHours = !NO_HOURS_DEPTS.includes(p.department);
        const det = (latest?.details ?? {}) as Record<string, unknown>;
        const description = (det.summary as string) ?? (det.task_type as string) ?? "";
        return {
          id: p.id,
          name: p.full_name || p.email,
          email: p.email,
          avatar: p.avatar_url,
          department: p.department,
          deptLabel: DEPARTMENT_LABELS[p.department],
          roleLabel: ROLE_LABELS[p.department] ?? "",
          count: list.length,
          showHours,
          minutes,
          latestTitle: latest?.title ?? "—",
          latestDesc: description,
          latestAt: latest?.created_at ?? "",
          status: stat,
        };
      })
      .filter((c) => {
        if (status !== "all" && c.status !== status) return false;
        if (term) {
          if (
            !c.name.toLowerCase().includes(term) &&
            !c.email.toLowerCase().includes(term)
          )
            return false;
        }
        return true;
      });

    if (sortKey === "name") built.sort((a, b) => a.name.localeCompare(b.name));
    else if (sortKey === "most") built.sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
    else if (sortKey === "least") built.sort((a, b) => a.count - b.count || a.name.localeCompare(b.name));
    else built.sort((a, b) => (a.latestAt < b.latestAt ? 1 : a.latestAt > b.latestAt ? -1 : a.name.localeCompare(b.name)));
    return built;
  }, [visibleEmployees, byUser, status, search, sortKey]);

  // KPI numbers — based on dept-filtered employees
  const visibleEmpIds = new Set(visibleEmployees.map((p) => p.id));
  const scopedEntries = entriesForDate.filter((e) => visibleEmpIds.has(e.user_id));
  const totalTasks = scopedEntries.length;
  const activeEmployees = new Set(scopedEntries.map((e) => e.user_id)).size;
  let completed = 0;
  let pending = 0;
  for (const e of scopedEntries) {
    const s = classifyEntryStatus(e);
    if (s === "completed") completed++;
    else pending++;
  }

  const apply = () => {
    setDate(pDate);
    setDept(pDept);
    setStatus(pStatus);
    setSearch(pSearch);
  };

  // ---- Access guard (after all hooks) ----
  if (loading) return null;
  const perms = (profile?.moderator_permissions ?? {}) as ModeratorPermissions;
  const isHr = profile?.department === "hr";
  const isMod = role === "moderator";
  const canSee =
    role === "admin" ||
    isHr ||
    (isMod &&
      (modCanWorkspace(perms, "view_overview") ||
        modCanWorkspace(perms, "view_activity_feed") ||
        modCanWorkspace(perms, "search_users")));
  if (!canSee) {
    return (
      <>
        <AppHeader subtitle="Today's Task" />
        <div className="px-[22px] pb-6">
          <div className="rounded-lg border border-border bg-card p-6 text-[13px] text-muted-foreground">
            Access restricted to Admin, Moderator, and HR.
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <AppHeader subtitle="Track what employees worked on today" />
      <div className="px-[22px] pb-8">
        <div className="mb-4">
          <h1 className="text-[22px] font-bold leading-tight">Today's Task</h1>
          <p className="mt-0.5 text-[12px] text-muted-foreground">
            Track what employees worked on today
          </p>
        </div>

        {/* Filter bar */}
        <div className="mb-4 flex flex-wrap items-end gap-3 rounded-lg border border-border bg-card p-3 shadow-sm">
          <div className="flex flex-col gap-1">
            <Label className="text-[10px] font-semibold uppercase text-muted-foreground">
              <CalendarIcon className="mr-1 inline h-3 w-3" />
              Date
            </Label>
            <Input
              type="date"
              value={pDate}
              onChange={(e) => setPDate(e.target.value)}
              className="h-9 w-[160px] text-[12px]"
            />
          </div>

          <div className="flex flex-col gap-1">
            <Label className="text-[10px] font-semibold uppercase text-muted-foreground">
              Department
            </Label>
            <Select
              value={pDept}
              onValueChange={(v) => setPDept(v as "all" | Department)}
              disabled={!!ownDeptLock}
            >
              <SelectTrigger className="h-9 w-[180px] text-[12px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(ownDeptLock
                  ? DEPT_OPTIONS.filter((o) => o.value === ownDeptLock)
                  : DEPT_OPTIONS
                ).map((o) => (
                  <SelectItem key={o.value} value={o.value} className="text-[12px]">
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-1">
            <Label className="text-[10px] font-semibold uppercase text-muted-foreground">
              Status
            </Label>
            <Select
              value={pStatus}
              onValueChange={(v) =>
                setPStatus(v as (typeof STATUS_OPTIONS)[number]["value"])
              }
            >
              <SelectTrigger className="h-9 w-[160px] text-[12px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value} className="text-[12px]">
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-1 flex-col gap-1 min-w-[200px]">
            <Label className="text-[10px] font-semibold uppercase text-muted-foreground">
              Search
            </Label>
            <div className="relative">
              <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search employee..."
                value={pSearch}
                onChange={(e) => setPSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") apply();
                }}
                className="h-9 pl-7 text-[12px]"
              />
            </div>
          </div>

          <Button
            onClick={apply}
            className="h-9 text-[12px] font-semibold text-white hover:opacity-90"
            style={{ background: "#7C3AED" }}
          >
            Apply
          </Button>
        </div>

        {/* Active filter chips + sort */}
        {(() => {
          const chips: { key: string; label: string; clear: () => void }[] = [];
          if (date !== today) {
            chips.push({ key: "date", label: date, clear: () => { setDate(today); setPDate(today); } });
          } else if (date === today) {
            // show "Today" chip only when other filters are active to mirror spec
          }
          if (dept !== "all") {
            const d = DEPT_OPTIONS.find((o) => o.value === dept);
            chips.push({ key: "dept", label: d?.label ?? dept, clear: () => { setDept("all"); setPDept("all"); } });
          }
          if (status !== "all") {
            const s = STATUS_OPTIONS.find((o) => o.value === status);
            chips.push({ key: "status", label: s?.label ?? status, clear: () => { setStatus("all"); setPStatus("all"); } });
          }
          if (search.trim()) {
            chips.push({ key: "search", label: `"${search.trim()}"`, clear: () => { setSearch(""); setPSearch(""); } });
          }
          // Show today chip alongside other chips
          if (chips.length && date === today) {
            chips.unshift({ key: "today", label: "Today", clear: () => {} });
          }
          return (
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <div className="flex flex-1 flex-wrap items-center gap-1.5">
                {chips.map((c) => (
                  <span
                    key={c.key}
                    className="inline-flex items-center gap-1 rounded-full border border-[#E5E7EB] bg-[#F5F3FF] px-2.5 py-1 text-[11px] font-medium text-[#6D28D9]"
                  >
                    {c.label}
                    {c.key !== "today" && (
                      <button
                        type="button"
                        onClick={c.clear}
                        className="ml-0.5 rounded-full text-[#7C3AED] hover:text-[#5B21B6]"
                        aria-label={`Clear ${c.label}`}
                      >
                        ×
                      </button>
                    )}
                  </span>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[11px] text-muted-foreground">Sort by</span>
                <Select value={sortKey} onValueChange={(v) => setSortKey(v as SortKey)}>
                  <SelectTrigger className="h-8 w-[160px] text-[12px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SORT_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value} className="text-[12px]">
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          );
        })()}

        {/* KPI cards */}
        <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {[
            { label: "Total Tasks Today", value: totalTasks },
            { label: "Employees Active Today", value: activeEmployees },
            { label: "Pending Tasks", value: pending },
            { label: "Completed Today", value: completed },
          ].map((k) => (
            <div
              key={k.label}
              className="rounded-[12px] border border-[#E5E7EB] bg-white p-4 shadow-sm"
            >
              <div className="text-[10px] font-semibold uppercase tracking-[0.4px] text-muted-foreground">
                {k.label}
              </div>
              <div className="mt-1 text-[26px] font-bold leading-none">{k.value}</div>
            </div>
          ))}
        </div>

        {/* Employee cards */}
        {isLoading ? (
          <div className="rounded-[12px] border border-[#E5E7EB] bg-white p-6 text-[13px] text-muted-foreground">
            Loading…
          </div>
        ) : cards.length === 0 ? (
          <div className="rounded-[12px] border border-[#E5E7EB] bg-white p-10 text-center">
            <div className="text-[14px] font-semibold text-foreground">
              No employees found for this filter.
            </div>
            <div className="mt-1 text-[12px] text-muted-foreground">
              Try changing date, department, or search.
            </div>
          </div>
        ) : (
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
            {cards.map((c) => {
              const pill = STATUS_PILL[c.status];
              const initial = c.name.charAt(0).toUpperCase();
              return (
                <div
                  key={c.id}
                  className="flex flex-col rounded-[12px] border border-[#E5E7EB] bg-white p-4 shadow-sm"
                >
                  <div className="flex items-start gap-3">
                    <div
                      className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full text-[13px] font-bold text-white"
                      style={{ background: "var(--gradient-avatar)" }}
                    >
                      {c.avatar ? (
                        <img
                          src={c.avatar}
                          alt=""
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        initial
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-[14px] font-semibold leading-tight">
                        {c.name}
                      </div>
                      <div className="mt-0.5 truncate text-[11px] text-muted-foreground">
                        {c.deptLabel}
                        {c.roleLabel ? ` · ${c.roleLabel}` : ""}
                      </div>
                    </div>
                    <span
                      className="shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold"
                      style={{ background: pill.bg, color: pill.fg }}
                    >
                      {pill.label}
                    </span>
                  </div>

                  <div className="mt-3 grid gap-1.5 text-[12px]">
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">
                        {NO_HOURS_DEPTS.includes(c.department)
                          ? "Tasks Uploaded"
                          : "Tasks Done"}
                      </span>
                      <span className="font-semibold">{c.count}</span>
                    </div>
                    {c.showHours && (
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">Hours Worked</span>
                        <span className="font-semibold">
                          {c.minutes > 0 ? fmtHM(c.minutes) : "—"}
                        </span>
                      </div>
                    )}
                    <div>
                      <div className="text-muted-foreground">Latest Task</div>
                      <div className="mt-0.5 truncate font-medium">{c.latestTitle}</div>
                      {c.latestDesc && (
                        <div className="mt-0.5 truncate text-[11px] text-muted-foreground">
                          {c.latestDesc}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="mt-4">
                    <button
                      type="button"
                      onClick={() => setOpenId(c.id)}
                      className="inline-flex h-8 w-full items-center justify-center gap-1 rounded-md border border-[#E5E7EB] bg-white text-[12px] font-semibold text-[#7C3AED] transition-colors hover:bg-[#F5F3FF]"
                    >
                      View Details →
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <TaskDetailModal
        open={!!openId}
        onClose={() => setOpenId(null)}
        card={openId ? cards.find((c) => c.id === openId) ?? null : null}
        entries={openId ? byUser.get(openId) ?? [] : []}
        date={date}
      />
    </>
  );
}

// ---------------- Task Detail Modal ----------------

type CardData = {
  id: string;
  name: string;
  email: string;
  avatar: string | null | undefined;
  department: Department;
  deptLabel: string;
  roleLabel: string;
  count: number;
  showHours: boolean;
  minutes: number;
  status: UiStatus;
};

function fmt12(hhmm: unknown): string | null {
  if (typeof hhmm !== "string") return null;
  const m = hhmm.match(/^(\d{1,2}):(\d{2})/);
  if (!m) return null;
  const h = Number(m[1]);
  const min = m[2];
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = ((h + 11) % 12) + 1;
  return `${String(h12).padStart(2, "0")}:${min} ${ampm}`;
}

function fmtDateLong(iso: string): string {
  const d = new Date(iso + "T00:00:00");
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" });
}

function linkLabelFor(taskType: string, sourceTable: string): string {
  const t = taskType.toLowerCase();
  if (t.includes("script")) return "Script Doc";
  if (t.includes("blog")) return "Blog Doc";
  if (t.includes("calendar")) return "Calendar Sheet";
  if (t.includes("campaign") || t.includes("ad")) return "Campaign Link";
  if (t.includes("research") || t.includes("competitor")) return "Report Doc";
  if (t.includes("brief")) return "Brief Doc";
  if (sourceTable.includes("designer") || sourceTable.includes("editor")) return "Drive Link";
  return "Open";
}

function TaskDetailModal({
  open,
  onClose,
  card,
  entries,
  date,
}: {
  open: boolean;
  onClose: () => void;
  card: CardData | null;
  entries: TeamEntry[];
  date: string;
}) {
  if (!card) return null;
  const pill = STATUS_PILL[card.status];
  const initial = card.name.charAt(0).toUpperCase();
  const isUploadDept = NO_HOURS_DEPTS.includes(card.department);

  const sorted = [...entries].sort((a, b) => {
    const ra = (a.details as Record<string, unknown> | undefined)?.raw as
      | Record<string, unknown>
      | undefined;
    const rb = (b.details as Record<string, unknown> | undefined)?.raw as
      | Record<string, unknown>
      | undefined;
    const sa = parseHHMM(ra?.start_time) ?? 0;
    const sb = parseHHMM(rb?.start_time) ?? 0;
    if (sa !== sb) return sa - sb;
    return (a.created_at ?? "") < (b.created_at ?? "") ? -1 : 1;
  });

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="flex max-h-[90vh] w-[92vw] max-w-[980px] flex-col gap-0 overflow-hidden bg-white p-0">
        <DialogHeader className="border-b border-[#E5E7EB] px-6 py-4">
          <DialogTitle className="text-[18px] font-bold text-foreground">
            Today's Work Details
          </DialogTitle>
          <DialogDescription className="text-[12px] text-muted-foreground">
            Complete task history for selected date
          </DialogDescription>
        </DialogHeader>

        {/* Employee summary */}
        <div className="flex flex-wrap items-center gap-4 border-b border-[#E5E7EB] bg-[#FAFAFB] px-6 py-4">
          <div
            className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-full text-[15px] font-bold text-white"
            style={{ background: "var(--gradient-avatar)" }}
          >
            {card.avatar ? (
              <img src={card.avatar} alt="" className="h-full w-full object-cover" />
            ) : (
              initial
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[15px] font-semibold leading-tight">{card.name}</div>
            <div className="mt-0.5 text-[12px] text-muted-foreground">
              {card.roleLabel ? `${card.roleLabel} · ` : ""}
              {card.deptLabel}
            </div>
            <div className="mt-1 text-[11px] text-muted-foreground">
              Date: <span className="font-medium text-foreground">{fmtDateLong(date)}</span>
            </div>
          </div>
          <div className="flex items-center gap-4 text-[12px]">
            <div>
              <div className="text-[10px] font-semibold uppercase text-muted-foreground">
                {isUploadDept ? "Total Uploads" : "Total Tasks"}
              </div>
              <div className="text-[18px] font-bold leading-none">{card.count}</div>
            </div>
            {!isUploadDept && (
              <div>
                <div className="text-[10px] font-semibold uppercase text-muted-foreground">
                  Total Hours
                </div>
                <div className="text-[18px] font-bold leading-none">
                  {card.minutes > 0 ? fmtHM(card.minutes) : "—"}
                </div>
              </div>
            )}
            <span
              className="rounded-full px-2.5 py-1 text-[11px] font-semibold"
              style={{ background: pill.bg, color: pill.fg }}
            >
              {pill.label}
            </span>
          </div>
        </div>

        {/* Task table — scrollable body */}
        <div className="flex-1 overflow-auto px-6 py-4">
          {sorted.length === 0 ? (
            <div className="rounded-md border border-[#E5E7EB] bg-white py-10 text-center text-[12px] text-muted-foreground">
              No tasks recorded on this date.
            </div>
          ) : (
            <div className="overflow-hidden rounded-md border border-[#E5E7EB]">
              <table className="w-full text-left text-[12px]">
                <thead className="bg-[#F9FAFB] text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 w-[36px]">#</th>
                    <th className="px-3 py-2">Task Category</th>
                    <th className="px-3 py-2">Task / Description</th>
                    <th className="px-3 py-2 whitespace-nowrap">Time / Upload</th>
                    <th className="px-3 py-2 whitespace-nowrap">Duration</th>
                    <th className="px-3 py-2">Status</th>
                    <th className="px-3 py-2">Notes / Link</th>
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((e, i) => {
                    const det = (e.details ?? {}) as Record<string, unknown>;
                    const raw = (det.raw ?? {}) as Record<string, unknown>;
                    const taskType = String(det.task_type ?? "—");
                    const sourceTable = String(det.source_table ?? e.source_table ?? "");
                    const desc = String(det.summary ?? e.title ?? "");
                    const link = (det.link as string | undefined) ?? undefined;
                    const start = fmt12(raw.start_time);
                    const end = fmt12(raw.end_time);
                    const sMin = parseHHMM(raw.start_time);
                    const eMin = parseHHMM(raw.end_time);
                    const dur = sMin != null && eMin != null && eMin > sMin ? eMin - sMin : 0;
                    const status = classifyEntryStatus(e);
                    const sp = STATUS_PILL[status];

                    // ---- Calendar assignment row branch ----
                    if (sourceTable === "calendar_entries") {
                      const priority = String(det.priority ?? "normal");
                      const calStatus = String(raw.status ?? "pending");
                      const calStatusCls: Record<string, string> = {
                        pending: "bg-gray-100 text-gray-600",
                        in_progress: "bg-blue-100 text-blue-700",
                        completed: "bg-green-100 text-green-700",
                      };
                      const calStatusLabel: Record<string, string> = {
                        pending: "Pending",
                        in_progress: "In Progress",
                        completed: "Completed",
                      };
                      return (
                        <tr
                          key={e.id}
                          className="border-t border-[#E5E7EB] border-l-4 border-l-purple-400 bg-purple-50 align-top"
                        >
                          <td className="px-3 py-2 text-muted-foreground">{i + 1}</td>
                          <td className="px-3 py-2">
                            <div className="flex items-center gap-1.5">
                              <CalendarDays className="h-4 w-4 text-purple-600" />
                              <span className="rounded-full bg-purple-100 px-2 py-0.5 text-[10px] font-semibold text-purple-700">
                                Calendar Assignment
                              </span>
                            </div>
                          </td>
                          <td className="px-3 py-2">
                            <div className="font-medium text-[#18181B]">{e.title}</div>
                            {desc && (
                              <div className="mt-0.5 text-[11px] text-muted-foreground">
                                {desc}
                              </div>
                            )}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-muted-foreground">—</td>
                          <td className="px-3 py-2 whitespace-nowrap text-muted-foreground">—</td>
                          <td className="px-3 py-2">
                            <div className="flex flex-wrap items-center gap-1">
                              <span
                                className={cn(
                                  "rounded-full px-2 py-0.5 text-[10px] font-semibold",
                                  calStatusCls[calStatus] ?? calStatusCls.pending,
                                )}
                              >
                                {calStatusLabel[calStatus] ?? "Pending"}
                              </span>
                              {priority === "urgent" && (
                                <span className="rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-semibold text-red-700">
                                  Urgent
                                </span>
                              )}
                              {priority === "high" && (
                                <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-700">
                                  High
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-3 py-2">
                            <Link
                              to="/calendar"
                              className="inline-flex items-center gap-1 text-[11px] font-medium text-purple-700 hover:underline"
                            >
                              Open Calendar
                              <ExternalLink className="h-3 w-3" />
                            </Link>
                          </td>
                        </tr>
                      );
                    }

                    return (
                      <tr key={e.id} className="border-t border-[#E5E7EB] align-top">
                        <td className="px-3 py-2 text-muted-foreground">{i + 1}</td>
                        <td className="px-3 py-2 font-medium">{taskType}</td>
                        <td className="px-3 py-2 text-foreground">{desc || "—"}</td>
                        <td className="px-3 py-2 whitespace-nowrap text-foreground">
                          {isUploadDept
                            ? "Uploaded"
                            : start && end
                            ? `${start} - ${end}`
                            : "—"}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-foreground">
                          {isUploadDept ? "—" : dur > 0 ? fmtHM(dur) : "—"}
                        </td>
                        <td className="px-3 py-2">
                          <span
                            className="rounded-full px-2 py-0.5 text-[10px] font-semibold"
                            style={{ background: sp.bg, color: sp.fg }}
                          >
                            {sp.label}
                          </span>
                        </td>
                        <td className="px-3 py-2">
                          {link ? (
                            <a
                              href={String(link)}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center gap-1 font-medium text-[#7C3AED] hover:underline"
                            >
                              {linkLabelFor(taskType, sourceTable)}
                              <ExternalLink className="h-3 w-3" />
                            </a>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <DialogFooter className="flex-row items-center justify-between border-t border-[#E5E7EB] bg-white px-6 py-3 sm:justify-between">
          <Button variant="outline" onClick={onClose} className="h-9 text-[12px]">
            Close
          </Button>
          <Link
            to="/today-tasks/$id"
            params={{ id: card.id }}
            search={{ date }}
            onClick={onClose}
            className="inline-flex h-9 items-center justify-center rounded-md px-4 text-[12px] font-semibold text-white hover:opacity-90"
            style={{ background: "#7C3AED" }}
          >
            View Summary →
          </Link>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
